import { Button } from "@/components/ui/button";
import { CalendarRange } from "@/components/CalendarRange";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useGetSelectedLabel } from "@/components/range-picker/hooks/useGetSelectedLabel";
import { resolveDefaultMonth } from "@/components/range-picker/utils";
import { CalendarIcon } from "lucide-react";
import { forwardRef, ReactNode, useCallback, useState } from "react";
import { DateRange, PropsRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { useDateRangeSelection } from "./hooks/useDateRangeSelection";
import { useDayShadeModifiers } from "./hooks/useDayShadeModifiers";

export type { DateBounds } from "./types";

type RangePickerProps = Omit<PropsRange, "mode" | "selected" | "onSelect"> & {
  selected?: DateRange;
  onChange(date: DateRange): void;
  startMonth?: Date;
  endMonth?: Date;
  maxRangeDays?: number;
  disabled?: { before: Date; after: Date } | true;
  defaultMonth?: Date;
  header?: ReactNode;
  className?: string;
};

export const RangePicker = forwardRef<HTMLButtonElement, RangePickerProps>(
  function RangePicker(
    {
      selected,
      onChange,
      startMonth,
      endMonth,
      maxRangeDays,
      disabled,
      defaultMonth,
      header,
      className,
      ...rest
    },
    ref,
  ) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<DateRange | undefined>(undefined);

    const onOpenChange = useCallback(
      (next: boolean) => {
        setOpen(next);
        if (next) {
          setDraft(selected);
        }
      },
      [selected],
    );

    const range = useDateRangeSelection({
      selected: draft,
      setSelected: setDraft,
      maxRangeDays,
      disabled,
      startMonth,
      endMonth,
    });

    const labelSelected = open ? draft : selected;
    const label = useGetSelectedLabel(labelSelected);

    const defaultMonthForCalendar = resolveDefaultMonth(
      defaultMonth,
      selected,
      startMonth,
      endMonth,
    );

    const { modifiers, modifiersClassNames } = useDayShadeModifiers({
      isDayDisabled: range.isDayDisabled,
      isOutsideMaxRangeWindow: range.isOutsideMaxRangeWindow,
    });

    const onApply = useCallback(() => {
      if (range.selected?.from && range.selected?.to) {
        onChange(range.selected);
        setOpen(false);
      }
    }, [range.selected, onChange]);

    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "justify-start text-left font-normal w-full",
              (!labelSelected?.from || !labelSelected?.to) &&
                "text-muted-foreground",
              className,
            )}
            ref={ref}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          {header}
          <CalendarRange
            {...rest}
            selected={range.selected}
            onSelect={range.onSelect}
            defaultMonth={defaultMonthForCalendar}
            startMonth={startMonth}
            endMonth={endMonth}
            disabled={range.isDayDisabled}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
          />
          <div className="p-3 pt-0">
            <Button
              className="w-full"
              onClick={onApply}
              variant="secondary"
              disabled={!range.canApply}
            >
              {t("datePicker.pickRange")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);
